import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { apiFetch, descargarArchivo, obtenerBlobUrl } from '../../lib/api';
import { Spinner } from '../../components/Spinner';
import { Modal } from '../../components/Modal';
import {
  comoLista,
  EDITION_TYPE_LABEL,
  fmtFecha,
  inputClass,
  labelClass,
  PRIORIDAD_CLASE,
  STATUS_CLASE,
  STATUS_LABEL,
  traducirError,
  type EditionDetail,
  type EditionItem,
  type PlazoClave,
  type Summary,
} from './tipos';

function prioridadDe(item: EditionItem) {
  return item.items.classifications?.[0]?.priority ?? null;
}

// ─────────────────────── Editor del resumen de un item ─────────────────────

const SUMMARY_VACIO: Summary = {
  titular: '',
  que_paso: '',
  a_quien_afecta: '',
  implicancia_practica: '',
  plazos_clave: [],
  contexto: '',
  url_fuente: '',
};

function EditarItem({
  editionId,
  item,
  onCerrar,
  onGuardado,
}: {
  editionId: string;
  item: EditionItem;
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const [s, setS] = useState<Summary>({ ...SUMMARY_VACIO, ...(item.summary ?? {}) });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const titularId = useId();
  const quePasoId = useId();
  const afectaId = useId();
  const implicanciaId = useId();
  const contextoId = useId();
  const urlId = useId();

  function set<K extends keyof Summary>(campo: K, valor: Summary[K]) {
    setS((prev) => ({ ...prev, [campo]: valor }));
  }

  function setPlazo(i: number, campo: keyof PlazoClave, valor: string) {
    setS((prev) => {
      const plazos = prev.plazos_clave.map((p, idx) =>
        idx === i ? { ...p, [campo]: valor } : p,
      );
      return { ...prev, plazos_clave: plazos };
    });
  }

  function agregarPlazo() {
    setS((prev) => ({ ...prev, plazos_clave: [...prev.plazos_clave, { fecha: '', hito: '' }] }));
  }

  function quitarPlazo(i: number) {
    setS((prev) => ({ ...prev, plazos_clave: prev.plazos_clave.filter((_, idx) => idx !== i) }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (enviando) return;
    setError(null);
    setEnviando(true);
    try {
      const summary: Summary = {
        ...s,
        plazos_clave: s.plazos_clave.filter((p) => p.fecha.trim() || p.hito.trim()),
      };
      await apiFetch(`/api/editions/${editionId}/items/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ summary }),
      });
      onGuardado();
    } catch (err) {
      setError(traducirError(err));
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Editar textos del item" onCerrar={onCerrar}>
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        <div>
          <label htmlFor={titularId} className={labelClass}>
            Titular
          </label>
          <input
            id={titularId}
            type="text"
            value={s.titular}
            onChange={(e) => set('titular', e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor={quePasoId} className={labelClass}>
            Qué pasó
          </label>
          <textarea
            id={quePasoId}
            rows={3}
            value={s.que_paso}
            onChange={(e) => set('que_paso', e.target.value)}
            className={`${inputClass} resize-y leading-relaxed`}
          />
        </div>

        <div>
          <label htmlFor={afectaId} className={labelClass}>
            A quién afecta
          </label>
          <textarea
            id={afectaId}
            rows={2}
            value={s.a_quien_afecta}
            onChange={(e) => set('a_quien_afecta', e.target.value)}
            className={`${inputClass} resize-y leading-relaxed`}
          />
        </div>

        <div>
          <label htmlFor={implicanciaId} className={labelClass}>
            Implicancia práctica
          </label>
          <textarea
            id={implicanciaId}
            rows={2}
            value={s.implicancia_practica}
            onChange={(e) => set('implicancia_practica', e.target.value)}
            className={`${inputClass} resize-y leading-relaxed`}
          />
        </div>

        <div>
          <span className={labelClass}>Plazos clave</span>
          <div className="mt-2 flex flex-col gap-2">
            {s.plazos_clave.map((p, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={p.fecha}
                  onChange={(e) => setPlazo(i, 'fecha', e.target.value)}
                  placeholder="Fecha"
                  aria-label={`Fecha del plazo ${i + 1}`}
                  className="w-36 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-texto outline-none focus:border-azul-profundo focus:ring-2 focus:ring-azul-profundo/25"
                />
                <input
                  type="text"
                  value={p.hito}
                  onChange={(e) => setPlazo(i, 'hito', e.target.value)}
                  placeholder="Hito"
                  aria-label={`Hito del plazo ${i + 1}`}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-texto outline-none focus:border-azul-profundo focus:ring-2 focus:ring-azul-profundo/25"
                />
                <button
                  type="button"
                  onClick={() => quitarPlazo(i)}
                  aria-label={`Quitar plazo ${i + 1}`}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gris transition-colors duration-150 hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="m6 6 12 12M18 6 6 18" />
                  </svg>
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={agregarPlazo}
              className="w-fit rounded-lg px-3 py-1.5 text-xs font-medium text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
            >
              + Agregar plazo
            </button>
          </div>
        </div>

        <div>
          <label htmlFor={contextoId} className={labelClass}>
            Contexto
          </label>
          <textarea
            id={contextoId}
            rows={2}
            value={s.contexto}
            onChange={(e) => set('contexto', e.target.value)}
            className={`${inputClass} resize-y leading-relaxed`}
          />
        </div>

        <div>
          <label htmlFor={urlId} className={labelClass}>
            URL de la fuente
          </label>
          <input
            id={urlId}
            type="url"
            value={s.url_fuente}
            onChange={(e) => set('url_fuente', e.target.value)}
            className={inputClass}
          />
        </div>

        <div className="mt-2 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCerrar}
            className="rounded-lg px-4 py-2.5 text-sm font-medium text-gris transition-colors duration-150 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={enviando}
            className="rounded-lg bg-azul-profundo px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
          >
            {enviando ? 'Guardando…' : 'Guardar textos'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ────────────────────────── Bloque de detalle ──────────────────────────────

function CampoResumen({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  if (!valor?.trim()) return null;
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-[0.15em] text-azul-medio">
        {etiqueta}
      </p>
      <p className="mt-1 text-sm font-light leading-relaxed text-texto">{valor}</p>
    </div>
  );
}

function BloqueQa({ item }: { item: EditionItem }) {
  const qa = item.qa_verdict;
  if (!qa) return null;
  const aprobado = qa.resultado === 'APROBADO';
  const omisiones = comoLista(qa.omisiones_criticas);
  const reparacion = comoLista(qa.instrucciones_reparacion);
  const mejoras = comoLista(qa.mejoras_opcionales);

  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span aria-hidden="true" className="flex h-6 w-6 items-center justify-center rounded-md bg-amber-50 text-[9px] font-semibold text-amber-700">
          N4
        </span>
        <span className="text-[11px] font-medium uppercase tracking-[0.15em] text-gris">
          Auditoría de Gabriela, la Auditora
        </span>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${
            aprobado
              ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
              : 'bg-red-50 text-red-700 ring-red-200'
          }`}
        >
          {aprobado ? 'APROBADO' : 'RECHAZADO'}
        </span>
      </div>

      {!aprobado && (
        <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-normal leading-relaxed text-red-800 ring-1 ring-inset ring-red-200">
          Gabriela rechazó la última versión de Violeta incluso tras los ciclos de reparación:
          las afirmaciones marcadas en rojo abajo no constan en la fuente. Por eso este item
          quedó con "Requiere revisión" — <span className="font-medium">no se envía sin tu
          visto bueno</span>: corrige los textos con "Editar textos" o descártalo.
        </p>
      )}

      {qa.verificacion_factual?.length > 0 && (
        <ul className="mt-3 flex flex-col gap-2">
          {qa.verificacion_factual.map((v, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <span
                aria-hidden="true"
                className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                  /respald|verific|correct|ok|verdad/i.test(v.estado)
                    ? 'bg-emerald-500'
                    : /contradi|no_encontrad|fals|error|incorrect/i.test(v.estado)
                      ? 'bg-red-500'
                      : 'bg-amber-500'
                }`}
              />
              <div>
                <p className="font-normal text-texto">{v.afirmacion}</p>
                <p className="text-xs font-light text-gris">
                  {v.estado}
                  {v.evidencia ? ` · ${v.evidencia}` : ''}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {omisiones.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-medium uppercase tracking-[0.15em] text-red-600">
            Omisiones críticas
          </p>
          <ul className="mt-1 list-disc pl-5 text-sm font-light text-texto">
            {omisiones.map((o, i) => (
              <li key={i}>{o}</li>
            ))}
          </ul>
        </div>
      )}

      {reparacion.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-medium uppercase tracking-[0.15em] text-amber-600">
            Instrucciones de reparación
          </p>
          <ul className="mt-1 list-disc pl-5 text-sm font-light text-texto">
            {reparacion.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {mejoras.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-medium uppercase tracking-[0.15em] text-gris">
            Mejoras opcionales
          </p>
          <ul className="mt-1 list-disc pl-5 text-sm font-light text-gris">
            {mejoras.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TarjetaItem({
  item,
  onEditar,
}: {
  item: EditionItem;
  onEditar: () => void;
}) {
  const s = item.summary;
  const prioridad = prioridadDe(item);
  const requiereHumano =
    item.items.needs_review || item.items.classifications?.some((c) => c.requires_human);
  const url = s?.url_fuente || item.items.url;
  const plazos = s?.plazos_clave ?? [];

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {item.incorporado_motivo && (
            <span className="chip bg-celeste/20 text-azul-profundo ring-celeste/60">
              Incorporada por el revisor
            </span>
          )}
          {item.republicado_de && (
            <span className="inline-flex items-center rounded-full border border-dashed border-amber-400 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700">
              Ya publicado · {item.republicado_de}
            </span>
          )}
          {prioridad && (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${PRIORIDAD_CLASE[prioridad]}`}
            >
              {prioridad}
            </span>
          )}
          {item.items.organism && (
            <span className="text-[11px] font-medium uppercase tracking-wider text-azul-medio">
              {item.items.organism}
            </span>
          )}
          {requiereHumano && (
            <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
              Requiere revisión
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onEditar}
          className="rounded-md px-2.5 py-1.5 text-xs font-medium text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
        >
          Editar textos
        </button>
      </div>

      <h3 className="mt-3 text-lg font-normal leading-snug text-azul-profundo">
        {s?.titular || item.items.title}
      </h3>

      <div className="mt-4 flex flex-col gap-4">
        <CampoResumen etiqueta="Qué pasó" valor={s?.que_paso ?? ''} />
        <CampoResumen etiqueta="A quién afecta" valor={s?.a_quien_afecta ?? ''} />
        <CampoResumen etiqueta="Implicancia práctica" valor={s?.implicancia_practica ?? ''} />

        {plazos.length > 0 && (
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.15em] text-azul-medio">
              Plazos clave
            </p>
            <ul className="mt-1.5 flex flex-col gap-1">
              {plazos.map((p, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <span className="font-medium text-texto">{p.fecha}</span>
                  <span className="font-light text-gris">{p.hito}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <CampoResumen etiqueta="Contexto" valor={s?.contexto ?? ''} />

        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-azul-profundo underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
          >
            Ver fuente
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M7 17 17 7M9 7h8v8" />
            </svg>
          </a>
        )}
      </div>

      <BloqueQa item={item} />
    </article>
  );
}

// ───────────────────────── Previsualización correo ─────────────────────────

function PreviewCorreo({ html, editionId, onCerrar, onPdf, descargandoPdf }: { html: string; editionId: string; onCerrar: () => void; onPdf?: () => void; descargandoPdf?: boolean }) {
  const tituloId = useId();
  // Vista Correo (HTML del email) o Vista PDF: el documento REAL en Carta, con la
  // paginación exacta que saldrá de la impresora — nada de bloques continuos.
  const [vista, setVista] = useState<'correo' | 'pdf'>('correo');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [cargandoPdf, setCargandoPdf] = useState(false);
  const [errorPdf, setErrorPdf] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    window.addEventListener('keydown', onKey);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflowPrevio;
    };
  }, [onCerrar]);

  // La object URL del PDF se libera al cerrar el modal.
  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  function abrirVistaPdf() {
    setVista('pdf');
    if (pdfUrl || cargandoPdf) return;
    setCargandoPdf(true);
    setErrorPdf(null);
    void obtenerBlobUrl(`/api/editions/${editionId}/pdf`)
      .then(setPdfUrl)
      .catch((e) => setErrorPdf(e instanceof Error ? e.message : 'No se pudo generar el PDF.'))
      .finally(() => setCargandoPdf(false));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center p-0 sm:items-center sm:p-6">
      <div className="fixed inset-0 bg-azul-noche/50" onClick={onCerrar} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={tituloId}
        className="relative flex h-full w-full max-w-3xl flex-col overflow-hidden bg-white shadow-xl sm:h-[85vh] sm:rounded-2xl"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
          <h2 id={tituloId} className="text-lg font-light text-azul-profundo">
            Previsualización
          </h2>
          <div role="tablist" aria-label="Formato de previsualización" className="flex rounded-full bg-slate-100 p-0.5">
            <button
              role="tab"
              type="button"
              aria-selected={vista === 'correo'}
              onClick={() => setVista('correo')}
              className={`pill !h-7 ${vista === 'correo' ? 'bg-white text-azul-profundo shadow-sm' : 'text-gris hover:text-azul-noche'}`}
            >
              Correo
            </button>
            <button
              role="tab"
              type="button"
              aria-selected={vista === 'pdf'}
              onClick={abrirVistaPdf}
              className={`pill !h-7 ${vista === 'pdf' ? 'bg-white text-azul-profundo shadow-sm' : 'text-gris hover:text-azul-noche'}`}
            >
              PDF (Carta)
            </button>
          </div>
          <div className="flex items-center gap-2">
            {onPdf && (
              <button type="button" onClick={onPdf} disabled={descargandoPdf} className="btn-secundario !h-8 !px-3.5 !text-[13px] disabled:cursor-not-allowed disabled:opacity-60">
                {descargandoPdf ? 'Generando PDF…' : 'Descargar PDF'}
              </button>
            )}
            <button
              type="button"
              onClick={onCerrar}
              aria-label="Cerrar"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gris transition-colors duration-150 hover:bg-slate-100 hover:text-azul-profundo focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          </div>
        </div>
        {vista === 'correo' ? (
          <iframe
            srcDoc={html}
            title="Previsualización del correo"
            sandbox=""
            className="h-full w-full flex-1 bg-white"
          />
        ) : cargandoPdf ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-[#EEF2F7]">
            <Spinner tamano="md" />
            <p className="text-sm font-normal text-gris">Componiendo el PDF en papel Carta…</p>
          </div>
        ) : errorPdf ? (
          <div className="flex flex-1 items-center justify-center bg-[#EEF2F7] p-6">
            <p role="alert" className="alerta-error">{errorPdf}</p>
          </div>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="Previsualización del PDF en papel Carta"
            className="h-full w-full flex-1 bg-[#EEF2F7]"
          />
        ) : null}
      </div>
    </div>
  );
}

// ─────────────────────── Confirmación de envío a clientes ───────────────────

function ConfirmarEnvio({
  onCerrar,
  onConfirmar,
  enviando,
}: {
  onCerrar: () => void;
  onConfirmar: () => void;
  enviando: boolean;
}) {
  const [total, setTotal] = useState<number | null>(null);

  useEffect(() => {
    let cancelado = false;
    void apiFetch<{ clients: { id: string }[] }>('/api/clients?active=true')
      .then((data) => {
        if (!cancelado) setTotal(data.clients.length);
      })
      .catch(() => {
        if (!cancelado) setTotal(null);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  return (
    <Modal titulo="Enviar a clientes" onCerrar={onCerrar}>
      <p className="text-sm font-light leading-relaxed text-texto">
        {total != null
          ? `Se enviará esta edición a ${total} ${total === 1 ? 'cliente activo' : 'clientes activos'}.`
          : 'Se enviará esta edición a los clientes activos.'}{' '}
        Esta acción no se puede deshacer.
      </p>
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-lg px-4 py-2.5 text-sm font-medium text-gris transition-colors duration-150 hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirmar}
          disabled={enviando}
          className="rounded-lg bg-azul-profundo px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
        >
          {enviando ? 'Enviando…' : 'Enviar ahora'}
        </button>
      </div>
    </Modal>
  );
}

// ──────────────────────────────── Detalle ──────────────────────────────────

// ── Incorporación por el revisor: rescatar noticias rechazadas por la editora ─────────

interface Rechazada {
  id: string;
  title: string | null;
  organism: string | null;
  publication_date: string | null;
  url: string | null;
  motivo: string;
  justificacion: string | null;
}

const MOTIVO_LABEL: Record<string, string> = {
  sin_efecto_regulatorio: 'Sin efecto regulatorio',
  acto_particular: 'Acto particular',
  duplicado: 'Duplicado',
  baja: 'En "Otros movimientos" (BAJA)',
};

type ModalIncorporar =
  | { fase: 'trabajando'; hechas: number; total: number }
  | { fase: 'listo'; resumen: string }
  | { fase: 'error'; msg: string };

function SeccionRechazadas({
  editionId,
  onIncorporado,
  onPrevisualizar,
  onAprobar,
}: {
  editionId: string;
  onIncorporado: () => void;
  onPrevisualizar: () => void;
  onAprobar: () => void;
}) {
  const [rechazadas, setRechazadas] = useState<Rechazada[]>([]);
  const [editable, setEditable] = useState(false);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<ModalIncorporar | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargarRechazadas = useCallback(async () => {
    try {
      const d = await apiFetch<{ editable: boolean; rechazadas: Rechazada[] }>(`/api/editions/${editionId}/rechazadas`);
      setRechazadas(d.rechazadas);
      setEditable(d.editable);
    } catch { /* sección accesoria: no rompe el detalle */ }
  }, [editionId]);

  useEffect(() => { void cargarRechazadas(); }, [cargarRechazadas]);

  function alternar(id: string) {
    setSeleccion((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else if (s.size < 10) s.add(id);
      return s;
    });
  }

  async function incorporar() {
    setError(null);
    const total = seleccion.size;
    setModal({ fase: 'trabajando', hechas: 0, total });
    try {
      const { job: j } = await apiFetch<{ job: { id: string } }>(`/api/editions/${editionId}/incorporar`, {
        method: 'POST',
        body: JSON.stringify({ item_ids: [...seleccion] }),
      });
      const poll = window.setInterval(() => {
        void (async () => {
          try {
            const d = await apiFetch<{ job: { status: string; error: string | null; detail?: { incorporar?: { hechas: number; total: number; resultados?: { ok: boolean; requiere_humano: boolean }[] } } } }>(`/api/newsletter/jobs/${j.id}`);
            const inc = d.job.detail?.incorporar;
            if (inc && d.job.status === 'running') setModal({ fase: 'trabajando', hechas: inc.hechas, total: inc.total });
            if (d.job.status === 'done' || d.job.status === 'error') {
              window.clearInterval(poll);
              if (d.job.status === 'error') {
                setModal({ fase: 'error', msg: d.job.error ?? 'La incorporación falló.' });
              } else {
                const rs = d.job.detail?.incorporar?.resultados ?? [];
                const ok = rs.filter((r) => r.ok).length;
                const rev = rs.filter((r) => r.requiere_humano).length;
                setModal({
                  fase: 'listo',
                  resumen: `${ok} ${ok === 1 ? 'noticia incorporada' : 'noticias incorporadas'} a la edición${rev ? ` — ${rev} con observaciones de Gabriela, revísala(s)` : ''}.`,
                });
              }
              setSeleccion(new Set());
              await cargarRechazadas();
              onIncorporado();
            }
          } catch { /* reintenta al próximo tick */ }
        })();
      }, 2500);
    } catch (e) {
      setModal({ fase: 'error', msg: traducirError(e) });
    }
  }

  if (!editable || rechazadas.length === 0) return null;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-sm font-medium text-azul-noche">
        Rechazadas por la editora{' '}
        <span className="font-normal text-gris">( {rechazadas.length} · haz click para seleccionar las que quieras rescatar )</span>
      </h2>
      <p className="mt-1 text-xs font-normal leading-relaxed text-gris">
        Marcela las dejó fuera de esta edición. Al incorporarlas, Violeta redacta la nota completa desde la
        fuente oficial y Gabriela la audita, igual que cualquier noticia — luego previsualiza y aprueba.
      </p>

      {error && <p role="alert" className="alerta-error mt-3">{error}</p>}

      {rechazadas.length > 0 && (
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {rechazadas.map((r) => {
            const activa = seleccion.has(r.id);
            return (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => alternar(r.id)}
                  disabled={Boolean(modal)}
                  aria-pressed={activa}
                  className={`w-full rounded-xl border p-3.5 text-left transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo ${
                    activa ? 'border-azul-profundo bg-celeste/15 shadow-sm' : 'border-slate-200 bg-white hover:border-celeste'
                  } ${modal ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <span className="flex items-start gap-2.5">
                    <span
                      aria-hidden="true"
                      className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] text-white ${
                        activa ? 'border-azul-profundo bg-azul-profundo' : 'border-slate-300 bg-white'
                      }`}
                    >
                      {activa ? '✓' : ''}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-normal leading-snug text-texto">{r.title ?? 'Sin título'}</span>
                      <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="chip bg-slate-100 text-slate-600 ring-slate-300">{MOTIVO_LABEL[r.motivo] ?? r.motivo}</span>
                        {r.organism && <span className="text-[11px] font-normal text-gris">{r.organism}</span>}
                      </span>
                      {r.justificacion && (
                        <span className="mt-1 block text-[11px] font-light leading-relaxed text-gris">{r.justificacion.slice(0, 160)}</span>
                      )}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {seleccion.size > 0 && !modal && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
          <p className="text-xs font-normal text-gris">{seleccion.size} {seleccion.size === 1 ? 'seleccionada' : 'seleccionadas'} (máx. 10)</p>
          <button type="button" onClick={() => void incorporar()} className="btn-primario">
            Incorporar {seleccion.size} {seleccion.size === 1 ? 'noticia' : 'noticias'}
          </button>
        </div>
      )}

      {/* Modal de progreso bloqueante: trabajando → listo (Previsualizar / Aprobar / Volver). */}
      {modal && (
        <Modal
          titulo={modal.fase === 'trabajando' ? 'Incorporando noticias' : modal.fase === 'listo' ? 'Noticias incorporadas' : 'La incorporación falló'}
          onCerrar={() => setModal(null)}
          bloqueante
        >
          {modal.fase === 'trabajando' && (
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <Spinner tamano="md" />
              <p className="text-sm font-normal leading-relaxed text-texto">
                Violeta está redactando desde la fuente oficial y Gabriela auditando cada nota…
              </p>
              <p className="text-lg font-medium tabular-nums text-azul-profundo">
                {modal.hechas}/{modal.total} <span className="text-sm font-normal text-gris">notas listas</span>
              </p>
              <p className="text-xs font-light text-gris">Esto toma alrededor de un minuto por noticia. No cierres esta ventana.</p>
            </div>
          )}
          {modal.fase === 'listo' && (
            <>
              <p role="status" className="rounded-lg bg-emerald-50/70 px-3 py-2.5 text-sm font-normal text-emerald-900 ring-1 ring-inset ring-emerald-200">
                ✓ {modal.resumen}
              </p>
              <p className="mt-3 text-sm font-normal leading-relaxed text-texto">
                El correo y el PDF ya están regenerados con las notas nuevas. ¿Qué quieres hacer?
              </p>
              <div className="mt-5 flex flex-col gap-2">
                <button type="button" onClick={() => { setModal(null); onPrevisualizar(); }} className="btn-primario w-full">
                  Previsualizar correo
                </button>
                <button type="button" onClick={() => { setModal(null); onAprobar(); }} className="btn-secundario w-full">
                  Aprobar edición
                </button>
                <button type="button" onClick={() => setModal(null)} className="btn-fantasma w-full">
                  Volver a la edición
                </button>
              </div>
            </>
          )}
          {modal.fase === 'error' && (
            <>
              <p role="alert" className="alerta-error">{modal.msg}</p>
              <div className="mt-4 flex justify-end">
                <button type="button" onClick={() => setModal(null)} className="btn-fantasma">Volver a la edición</button>
              </div>
            </>
          )}
        </Modal>
      )}
    </section>
  );
}

export default function DetalleEdicion({
  editionId,
  onVolver,
  onCambio,
  previewInicial = false,
}: {
  editionId: string;
  onVolver: () => void;
  onCambio: () => void;
  /** Abre la vista previa de inmediato (acción "Ver" de la tabla). */
  previewInicial?: boolean;
}) {
  const [edicion, setEdicion] = useState<EditionDetail | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editandoItem, setEditandoItem] = useState<EditionItem | null>(null);

  const [title, setTitle] = useState('');
  const [editorial, setEditorial] = useState('');
  const [guardandoMeta, setGuardandoMeta] = useState(false);
  const [metaOk, setMetaOk] = useState(false);

  const [aprobando, setAprobando] = useState(false);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  const [previewAbierto, setPreviewAbierto] = useState(previewInicial);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  const [confirmEnvio, setConfirmEnvio] = useState(false);
  const [accionFlujo, setAccionFlujo] = useState<'submit' | 'send' | null>(null);
  const [mensajeFlujo, setMensajeFlujo] = useState<string | null>(null);

  const titleId = useId();
  const editorialId = useId();

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await apiFetch<{ edition: EditionDetail }>(`/api/editions/${editionId}`);
      setEdicion(data.edition);
      setTitle(data.edition.title ?? '');
      setEditorial(data.edition.editorial ?? '');
    } catch (err) {
      setError(traducirError(err));
    } finally {
      setCargando(false);
    }
  }, [editionId]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function guardarMeta() {
    setErrorAccion(null);
    setMetaOk(false);
    setGuardandoMeta(true);
    try {
      await apiFetch(`/api/editions/${editionId}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: title.trim() || null, editorial: editorial.trim() || null }),
      });
      setMetaOk(true);
      onCambio();
    } catch (err) {
      setErrorAccion(traducirError(err));
    } finally {
      setGuardandoMeta(false);
    }
  }

  async function aprobar() {
    setErrorAccion(null);
    setMensajeFlujo(null);
    setAprobando(true);
    try {
      await apiFetch(`/api/editions/${editionId}/approve`, { method: 'POST' });
      await cargar();
      onCambio();
    } catch (err) {
      setErrorAccion(traducirError(err));
    } finally {
      setAprobando(false);
    }
  }

  async function enviarAprobacion() {
    setErrorAccion(null);
    setMensajeFlujo(null);
    setAccionFlujo('submit');
    try {
      const res = await apiFetch<{ ok: boolean; sent_to: string }>(
        `/api/editions/${editionId}/submit-review`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      setMensajeFlujo(`Enviado a aprobación a ${res.sent_to}.`);
      await cargar();
      onCambio();
    } catch (err) {
      setErrorAccion(traducirError(err));
    } finally {
      setAccionFlujo(null);
    }
  }

  async function enviarClientes() {
    setErrorAccion(null);
    setMensajeFlujo(null);
    setAccionFlujo('send');
    try {
      const res = await apiFetch<{ ok: boolean; total: number; sent: number; failed: number }>(
        `/api/editions/${editionId}/send`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      setConfirmEnvio(false);
      setMensajeFlujo(`Enviado: ${res.sent} ok, ${res.failed} con error (de ${res.total}).`);
      await cargar();
      onCambio();
    } catch (err) {
      setErrorAccion(traducirError(err));
    } finally {
      setAccionFlujo(null);
    }
  }

  // Las filas de "otros movimientos" (summary null, section otros_movimientos) son candado
  // de no-repetición, no tarjetas: se muestran aparte como lista compacta.
  const items = (edicion?.edition_items ?? []).filter((it) => it.section !== 'otros_movimientos');
  const otrosMovimientos = edicion?.otros_movimientos ?? [];
  const puedeAprobar =
    edicion != null && (edicion.status === 'draft' || edicion.status === 'in_review');

  return (
    <div className="flex flex-col gap-6">
      <button
        type="button"
        onClick={onVolver}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-azul-profundo transition-colors duration-150 hover:text-azul-profundo/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 18 9 12l6-6" />
        </svg>
        Volver a ediciones
      </button>

      {cargando ? (
        <div
          role="status"
          aria-live="polite"
          className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-sm font-light uppercase tracking-[0.2em] text-gris shadow-sm"
        >
          Cargando edición…
        </div>
      ) : error || !edicion ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
          <p className="text-sm text-red-700">{error ?? 'No se encontró la edición.'}</p>
          <button
            type="button"
            onClick={() => void cargar()}
            className="mt-4 rounded-lg border border-azul-profundo px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <>
          {/* Cabecera + metadatos */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-azul-medio">
                  {EDITION_TYPE_LABEL[edicion.type]} · N° {edicion.edition_number}
                </span>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${STATUS_CLASE[edicion.status]}`}
                >
                  {STATUS_LABEL[edicion.status]}
                </span>
              </div>
              <p className="text-xs font-light text-gris">
                {edicion.generada_por ? `Generada por ${edicion.generada_por} · ` : 'Programada · Víctor, el Director · '}
                Período {fmtFecha(edicion.period_start)} – {fmtFecha(edicion.period_end)}
              </p>
            </div>

            {errorAccion && (
              <div
                role="alert"
                className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {errorAccion}
              </div>
            )}

            <div className="mt-5 flex flex-col gap-5">
              <div>
                <label htmlFor={titleId} className={labelClass}>
                  Título
                </label>
                <input
                  id={titleId}
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setMetaOk(false);
                  }}
                  placeholder="Título de la edición"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor={editorialId} className={labelClass}>
                  Editorial
                </label>
                <textarea
                  id={editorialId}
                  rows={3}
                  value={editorial}
                  onChange={(e) => {
                    setEditorial(e.target.value);
                    setMetaOk(false);
                  }}
                  placeholder="Texto editorial de apertura…"
                  className={`${inputClass} resize-y leading-relaxed`}
                />
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                {metaOk && (
                  <span className="text-sm font-light text-emerald-700">Cambios guardados.</span>
                )}
                <button
                  type="button"
                  onClick={() => void guardarMeta()}
                  disabled={guardandoMeta}
                  className="rounded-lg border border-azul-profundo px-4 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {guardandoMeta ? 'Guardando…' : 'Guardar edición'}
                </button>
              </div>
            </div>
          </section>

          {/* Flujo de publicación según estado */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-sm font-medium uppercase tracking-[0.2em] text-azul-medio">
              Publicación
            </h2>

            {items.some((it) => it.republicado_de) && edicion.status !== 'sent' && (
              <div
                role="status"
                className="mt-4 rounded-lg border border-dashed border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              >
                <span className="font-medium">
                  {items.filter((it) => it.republicado_de).length} de {items.length} contenidos ya fueron publicados
                  anteriormente
                </span>{' '}
                (corrida manual de prueba: se incluyen para que revises el resultado completo). Verifica antes de
                aprobar — los clientes no deben recibir dos veces el mismo hecho.
              </div>
            )}

            {edicion.rejected_reason && edicion.status === 'draft' && (
              <div
                role="status"
                className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              >
                <span className="font-medium">Rechazada anteriormente:</span>{' '}
                {edicion.rejected_reason}
              </div>
            )}

            {edicion.status === 'in_review' && (
              <div
                role="status"
                className="mt-4 rounded-lg border border-celeste bg-celeste/20 px-4 py-3 text-sm text-azul-profundo"
              >
                En revisión — se envió un correo de aprobación al aprobador configurado en
                Ajustes. La aprobación o el rechazo se realizan desde ese correo.
              </div>
            )}

            {edicion.status === 'sent' && (
              <div
                role="status"
                className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
              >
                Enviada el {fmtFecha(edicion.sent_at)}.
              </div>
            )}

            {mensajeFlujo && (
              <div
                role="status"
                className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800"
              >
                {mensajeFlujo}
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-3">
              {edicion.html && (
                <button
                  type="button"
                  onClick={() => setPreviewAbierto(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-azul-profundo px-4 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M2 12s3.5-7.5 10-7.5S22 12 22 12s-3.5 7.5-10 7.5S2 12 2 12Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  Previsualizar correo
                </button>
              )}

              {edicion.status === 'draft' && (
                <button
                  type="button"
                  onClick={() => void enviarAprobacion()}
                  disabled={accionFlujo === 'submit'}
                  className="rounded-lg bg-azul-profundo px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {accionFlujo === 'submit' ? 'Enviando…' : 'Enviar a aprobación'}
                </button>
              )}

              {edicion.status === 'approved' && (
                <button
                  type="button"
                  onClick={() => setConfirmEnvio(true)}
                  className="rounded-lg bg-azul-profundo px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-azul-profundo/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-azul-profundo"
                >
                  Enviar a clientes
                </button>
              )}
            </div>

            {/* Acción manual secundaria: aprobación in-app (el flujo primario es por correo). */}
            {puedeAprobar && (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="text-xs font-light text-gris">
                  ¿Prefieres aprobar sin pasar por el correo? Puedes hacerlo manualmente.
                </p>
                <button
                  type="button"
                  onClick={() => void aprobar()}
                  disabled={aprobando}
                  className="mt-2 rounded-lg px-3 py-2 text-xs font-medium text-azul-profundo transition-colors duration-150 hover:bg-azul-profundo/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-azul-profundo disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {aprobando ? 'Aprobando…' : 'Aprobar edición manualmente'}
                </button>
              </div>
            )}
          </section>

          {/* Previsualización de items */}
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-medium uppercase tracking-[0.2em] text-azul-medio">
              Contenidos ({items.length})
            </h2>
          </div>

          {items.length === 0 && otrosMovimientos.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-16 text-center">
              <p className="text-sm font-light text-gris">
                Esta edición no tiene contenidos.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {items.map((it) => (
                <TarjetaItem key={it.id} item={it} onEditar={() => setEditandoItem(it)} />
              ))}
            </div>
          )}

          {/* Rescatar noticias rechazadas por la editora (solo borrador/en revisión). */}
          <SeccionRechazadas
            editionId={editionId}
            onIncorporado={() => void cargar()}
            onPrevisualizar={() => setPreviewAbierto(true)}
            onAprobar={() => void aprobar()}
          />

          {otrosMovimientos.length > 0 && (
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-sm font-medium text-azul-noche">
                Otros movimientos del día{' '}
                <span className="font-normal text-gris">( {otrosMovimientos.length} · solo titular, sin análisis )</span>
              </h2>
              <ul className="mt-3 divide-y divide-slate-100">
                {otrosMovimientos.map((o, i) => (
                  <li key={i} className="flex flex-wrap items-baseline gap-x-2 py-2 text-sm font-light text-texto">
                    <span aria-hidden="true" className="text-azul-medio">›</span>
                    <span className="min-w-0 flex-1">{o.titulo}</span>
                    {o.organismo && <span className="text-xs text-gris">{o.organismo}</span>}
                    {o.url && (
                      <a href={o.url} target="_blank" rel="noreferrer" className="text-xs font-normal text-azul-medio hover:underline">
                        Ver fuente →
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {editandoItem && (
        <EditarItem
          editionId={editionId}
          item={editandoItem}
          onCerrar={() => setEditandoItem(null)}
          onGuardado={() => {
            setEditandoItem(null);
            void cargar();
            onCambio();
          }}
        />
      )}

      {previewAbierto && edicion?.html && (
        <PreviewCorreo
          html={edicion.html}
          editionId={editionId}
          onCerrar={() => setPreviewAbierto(false)}
          descargandoPdf={descargandoPdf}
          onPdf={() => {
            setDescargandoPdf(true);
            void descargarArchivo(`/api/editions/${editionId}/pdf`, `AMBEC-${edicion.type}-${String(edicion.edition_number).padStart(3, '0')}.pdf`)
              .catch(() => undefined)
              .finally(() => setDescargandoPdf(false));
          }}
        />
      )}

      {confirmEnvio && (
        <ConfirmarEnvio
          onCerrar={() => setConfirmEnvio(false)}
          onConfirmar={() => void enviarClientes()}
          enviando={accionFlujo === 'send'}
        />
      )}
    </div>
  );
}
